import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useCopy } from './copy/copyMode';
import { LayoutGrid, MessageSquare, FolderOpen, Bot, Plus, Upload, ChevronDown, Activity, BarChart3, Kanban, AlertCircle, TrendingUp, Users, DollarSign, Zap, Target, Map, MessageCircle, HeartPulse, Ticket, Gauge, Plug } from 'lucide-react';
type Persona = 'Leadership' | 'Sales' | 'Marketing' | 'Product' | 'Engineering' | 'Customer Success' | 'Operations';
interface AppCard {
  title: string;
  description: string;
  icon: React.ReactNode;
  connections: number;
  uiPreview: React.ReactNode;
}
const personaData: Record<Persona, AppCard[]> = {
  Sales: [{
    title: 'GTM Pipeline Tracker',
    description: 'Kanban and funnel pipeline with deal forecasting, signal alerts, and account drill-downs.',
    icon: <Kanban size={16} className="text-neutral-400" data-id="element-4579" data-mp="3ayzk28o7jgie" />,
    connections: 4,
    uiPreview: <div className="flex flex-col gap-3 h-full" data-id="element-4580" data-mp="36vjpdhvp7q8c">
          {/* Top stats */}
          <div className="flex gap-4 mb-2" data-id="element-4581" data-mp="14qj80qyrb5yf">
            <div className="flex flex-col" data-id="element-4582" data-mp="14ru92j0thzlu">
              <span className="text-[10px] text-neutral-500" data-id="element-4583" data-mp="19pvrd1qf5uw9">
                Total Pipeline
              </span>
              <span className="text-sm font-bold text-[#333333]" data-id="element-4584" data-mp="vr4hjrilavdx">$2.4M</span>
            </div>
            <div className="flex flex-col" data-id="element-4585" data-mp="zrevbqcmiq3c">
              <span className="text-[10px] text-neutral-500" data-id="element-4586" data-mp="zq4x915rthwp">Active Deals</span>
              <span className="text-sm font-bold text-[#333333]" data-id="element-4587" data-mp="lzrq310pe78f">18</span>
            </div>
            <div className="flex flex-col" data-id="element-4588" data-mp="qsbbul9jo8ob">
              <span className="text-[10px] text-neutral-500" data-id="element-4589" data-mp="qt2z98szn03i">Win Rate</span>
              <span className="text-sm font-bold text-green-600" data-id="element-4590" data-mp="c3ycrff5u9pl">32%</span>
            </div>
          </div>
          {/* Funnel bars */}
          <div className="flex flex-col gap-2 flex-1 justify-end" data-id="element-4591" data-mp="31p3stzjioeb">
            <div className="flex items-center gap-2" data-id="element-4592" data-mp="32h0670yoq5n">
              <span className="text-[9px] text-neutral-500 w-12" data-id="element-4593" data-mp="811qneqevvgw">
                Discovery
              </span>
              <div className="h-2 bg-blue-500 rounded-full w-full" data-id="element-4594" data-mp="29qzhfg2fqv7f" />
            </div>
            <div className="flex items-center gap-2" data-id="element-4595" data-mp="24yzc183lja3g">
              <span className="text-[9px] text-neutral-500 w-12" data-id="element-4596" data-mp="24x4u82nmfy3l">Demo</span>
              <div className="h-2 bg-indigo-500 rounded-full w-[75%]" data-id="element-4597" data-mp="200o10u1ketjn" />
            </div>
            <div className="flex items-center gap-2" data-id="element-4598" data-mp="1vzud40o4heqr">
              <span className="text-[9px] text-neutral-500 w-12" data-id="element-4599" data-mp="2sojy3zv1qngy">Proposal</span>
              <div className="h-2 bg-purple-500 rounded-full w-[45%]" data-id="element-4600" data-mp="2smfwxpoj82kd" />
            </div>
            <div className="flex items-center gap-2" data-id="element-4601" data-mp="2nnmgbody7vfz">
              <span className="text-[9px] text-neutral-500 w-12" data-id="element-4602" data-mp="2jnc968ef6yzj">
                Negotiation
              </span>
              <div className="h-2 bg-pink-500 rounded-full w-[20%]" data-id="element-4603" data-mp="2eo96gulwx4uq" />
            </div>
          </div>
        </div>
  }, {
    title: 'Deal War Room',
    description: 'Live deal command center with signals, stakeholders, and next-step plays.',
    icon: <Activity size={16} className="text-neutral-400" data-id="element-4604" data-mp="3ex7if5ms1bni" />,
    connections: 3,
    uiPreview: <div className="flex flex-col gap-3 h-full" data-id="element-4605" data-mp="3eve91iks4dbt">
          {/* Table header */}
          <div className="grid grid-cols-4 gap-2 border-b border-neutral-200 pb-2" data-id="element-4606" data-mp="3jwk2jim9k4me">
            <span className="text-[9px] text-neutral-500 col-span-2" data-id="element-4607" data-mp="3nwcg69qil0ym">
              ACCOUNT
            </span>
            <span className="text-[9px] text-neutral-500" data-id="element-4608" data-mp="3spx5zehyeomq">VALUE</span>
            <span className="text-[9px] text-neutral-500" data-id="element-4609" data-mp="1h9k8xfswzen">STATUS</span>
          </div>
          {/* Table rows */}
          <div className="flex flex-col gap-2" data-id="element-4610" data-mp="6fujatjbhd1i">
            <div className="grid grid-cols-4 gap-2 items-center" data-id="element-4611" data-mp="bgrzx1oohz0w">
              <div className="flex items-center gap-1.5 col-span-2" data-id="element-4612" data-mp="bha88wom83p6">
                <div className="w-1.5 h-1.5 rounded-full bg-green-500" data-id="element-4613" data-mp="fgsq1jl53yns" />
                <span className="text-[10px] text-[#333333] font-medium truncate" data-id="element-4614" data-mp="2d8dfrd37l25i">
                  Acme Corp
                </span>
              </div>
              <span className="text-[10px] text-neutral-600" data-id="element-4615" data-mp="2h9ql7m2e33bw">$120k</span>
              <span className="text-[9px] text-[#433832] bg-[#FAF8F4] px-1.5 py-0.5 rounded border border-[#EAE4D9]" data-id="element-4616" data-mp="1lxz7rp6j7kak">
                Review
              </span>
            </div>
            <div className="grid grid-cols-4 gap-2 items-center" data-id="element-4617" data-mp="1vxq3fu6tw5c8">
              <div className="flex items-center gap-1.5 col-span-2" data-id="element-4618" data-mp="1qydijezip8ca">
                <div className="w-1.5 h-1.5 rounded-full bg-yellow-500" data-id="element-4619" data-mp="1qz6gbqgi0nia" />
                <span className="text-[10px] text-[#333333] font-medium truncate" data-id="element-4620" data-mp="139u9gx8n8rj5">
                  Globex
                </span>
              </div>
              <span className="text-[10px] text-neutral-600" data-id="element-4621" data-mp="y7v5pm8zrslo">$85k</span>
              <span className="text-[9px] text-[#433832] bg-[#FAF8F4] px-1.5 py-0.5 rounded border border-[#EAE4D9]" data-id="element-4622" data-mp="1czx0irw7fveu">
                Proposal
              </span>
            </div>
            <div className="grid grid-cols-4 gap-2 items-center" data-id="element-4623" data-mp="kh048v0tods4">
              <div className="flex items-center gap-1.5 col-span-2" data-id="element-4624" data-mp="fjq70t76yxtq">
                <div className="w-1.5 h-1.5 rounded-full bg-red-500" data-id="element-4625" data-mp="fhdremowozju" />
                <span className="text-[10px] text-[#333333] font-medium truncate" data-id="element-4626" data-mp="2r50i8nh7v4ch">
                  Initech
                </span>
              </div>
              <span className="text-[10px] text-neutral-600" data-id="element-4627" data-mp="2i65c1be69u96">$250k</span>
              <span className="text-[9px] text-[#433832] bg-[#FAF8F4] px-1.5 py-0.5 rounded border border-[#EAE4D9]" data-id="element-4628" data-mp="2i3smod634hm3">
                Stalled
              </span>
            </div>
          </div>
        </div>
  }],
  Marketing: [{
    title: 'Campaign ROI Tracker',
    description: 'Real-time view of ad spend vs CAC across Meta, Google, and LinkedIn.',
    icon: <TrendingUp size={16} className="text-neutral-400" data-id="element-4629" data-mp="30vbncdcmzjky" />,
    connections: 5,
    uiPreview: <div className="flex flex-col gap-3 h-full" data-id="element-4630" data-mp="35uoidldnvwym">
          <div className="flex justify-between items-end mb-2" data-id="element-4631" data-mp="35xaqbm4gsehd">
            <div className="flex flex-col" data-id="element-4632" data-mp="1pgewcxww6kay">
              <span className="text-[10px] text-neutral-500" data-id="element-4633" data-mp="1ugay0qi49znc">Total Spend</span>
              <span className="text-sm font-bold text-[#333333]" data-id="element-4634" data-mp="1ue7xvdn92q4w">$45.2k</span>
            </div>
            <div className="flex flex-col text-right" data-id="element-4635" data-mp="3w4p1iha2ho2l">
              <span className="text-[10px] text-neutral-500" data-id="element-4636" data-mp="3r63xe6rp2k8v">Blended CAC</span>
              <span className="text-sm font-bold text-green-600" data-id="element-4637" data-mp="3mff6otnu9ras">$124</span>
            </div>
          </div>
          {/* Bar chart mockup */}
          <div className="flex items-end gap-2 h-16 mt-auto border-b border-neutral-200 pb-1" data-id="element-4638" data-mp="3idk65rdqfhy2">
            {[40, 70, 45, 90, 65, 85, 100].map((h, i) => <div key={i} className="flex-1 bg-pink-400 rounded-t-sm" style={{
          height: `${h}%`
        }} data-id="element-4639" data-mp="38fvxdxjak9ww" data-mpi={i} />)}
          </div>
        </div>
  }, {
    title: 'Content Engine',
    description: 'Pipeline of drafts, published assets, and engagement metrics from social.',
    icon: <BarChart3 size={16} className="text-neutral-400" data-id="element-4640" data-mp="2zhakahwl9n3i" />,
    connections: 3,
    uiPreview: <div className="flex flex-col gap-3 h-full" data-id="element-4641" data-mp="11on2we9did4t">
          <div className="grid grid-cols-2 gap-2" data-id="element-4642" data-mp="xnbbq4aupjui">
            <div className="bg-white rounded p-2 border border-neutral-200 shadow-sm" data-id="element-4643" data-mp="xomfi44aqrlm">
              <span className="text-[9px] text-neutral-500 block mb-1" data-id="element-4644" data-mp="2fky2u3htdxqq">
                Drafts
              </span>
              <span className="text-xs font-bold text-[#333333]" data-id="element-4645" data-mp="2kig375hrn4n4">12</span>
            </div>
            <div className="bg-white rounded p-2 border border-neutral-200 shadow-sm" data-id="element-4646" data-mp="2pc2ht37hpilk">
              <span className="text-[9px] text-neutral-500 block mb-1" data-id="element-4647" data-mp="2tccroqyekt5p">
                Published
              </span>
              <span className="text-xs font-bold text-[#333333]" data-id="element-4648" data-mp="1wooln7bm8r2i">48</span>
            </div>
          </div>
          <div className="flex flex-col gap-1.5 mt-2" data-id="element-4649" data-mp="21njbuubwxhwn">
            <div className="h-1.5 w-full bg-neutral-100 rounded-full overflow-hidden" data-id="element-4650" data-mp="25mido62ckyjv">
              <div className="h-full bg-orange-400 w-[60%]" data-id="element-4651" data-mp="2ammtdtvflp91" />
            </div>
            <span className="text-[8px] text-neutral-500 text-right" data-id="element-4652" data-mp="3h2p2qaoy360i">
              60% to monthly goal
            </span>
          </div>
        </div>
  }],
  Engineering: [{
    title: 'Incident Command',
    description: 'Aggregated alerts from Sentry and Datadog with auto-triage status.',
    icon: <AlertCircle size={16} className="text-neutral-400" data-id="element-4653" data-mp="2sq533rbpb4ze" />,
    connections: 4,
    uiPreview: <div className="flex flex-col gap-2 h-full" data-id="element-4654" data-mp="36h9uk64x0qyn">
          <div className="bg-red-50 border border-red-100 rounded p-2 flex gap-2 items-start" data-id="element-4655" data-mp="36iuobtjeo5vg">
            <AlertCircle size={12} className="text-red-500 mt-0.5 shrink-0" data-id="element-4656" data-mp="31prvjnmgd0gb" />
            <div data-id="element-4657" data-mp="3geo9u2f3r9pd">
              <span className="text-[10px] font-medium text-red-700 block" data-id="element-4658" data-mp="3ge632butdd37">
                API Latency Spike
              </span>
              <span className="text-[8px] text-red-500" data-id="element-4659" data-mp="3bih5ge2cu69j">
                US-East-1 · 2m ago
              </span>
            </div>
          </div>
          <div className="bg-white border border-neutral-200 shadow-sm rounded p-2 flex gap-2 items-start" data-id="element-4660" data-mp="3leaomgakwy0a">
            <Zap size={12} className="text-yellow-500 mt-0.5 shrink-0" data-id="element-4661" data-mp="1v35zewmlcws0" />
            <div data-id="element-4662" data-mp="1r0sz4nqm5r2m">
              <span className="text-[10px] font-medium text-[#333333] block" data-id="element-4663" data-mp="1r1ay3s4nuje8">
                DB Connection Pool
              </span>
              <span className="text-[8px] text-neutral-500" data-id="element-4664" data-mp="1gxe4grnv6cu9">
                Warning · 15m ago
              </span>
            </div>
          </div>
        </div>
  }, {
    title: 'Sprint Velocity',
    description: 'Linear burn-down charts and PR review bottlenecks from GitHub.',
    icon: <Zap size={16} className="text-neutral-400" data-id="element-4665" data-mp="1c4ky5zcqywg2" />,
    connections: 2,
    uiPreview: <div className="flex flex-col gap-3 h-full" data-id="element-4666" data-mp="x7k800a7f9jp">
          <div className="flex justify-between items-center mb-1" data-id="element-4667" data-mp="xayad8ivbir5">
            <span className="text-[10px] text-neutral-500" data-id="element-4668" data-mp="1299j1fxffn7s">Sprint 42</span>
            <span className="text-[9px] text-green-700 bg-green-50 border border-green-100 px-1.5 py-0.5 rounded" data-id="element-4669" data-mp="o8ytcg7useo7">
              On Track
            </span>
          </div>
          {/* Line chart mockup */}
          <div className="relative h-16 mt-auto border-l border-b border-neutral-200" data-id="element-4670" data-mp="2in9i6oog78tz">
            <svg className="absolute inset-0 h-full w-full" preserveAspectRatio="none" viewBox="0 0 100 100" data-id="element-4671" data-mp="2nlaxqzmo0q0p">
              <path d="M0,10 L20,30 L40,25 L60,60 L80,55 L100,90" fill="none" stroke="#22c55e" strokeWidth="2" vectorEffect="non-scaling-stroke" data-id="element-4672" data-mp="1zg34is73aauo" />
              <path d="M0,0 L100,100" fill="none" stroke="#e5e5e5" strokeWidth="1" strokeDasharray="4 4" vectorEffect="non-scaling-stroke" data-id="element-4673" data-mp="249fq0tgh21j7" />
            </svg>
          </div>
        </div>
  }],
  Operations: [{
    title: 'Vendor Spend',
    description: 'Consolidated SaaS and vendor spend from Xero, Ramp, and Stripe.',
    icon: <DollarSign size={16} className="text-neutral-400" data-id="element-4674" data-mp="2n6z5ofxfqiel" />,
    connections: 3,
    uiPreview: <div className="flex flex-col gap-3 h-full" data-id="element-4675" data-mp="296y3mx5fp4xj">
          <div className="flex items-baseline gap-2 mb-2" data-id="element-4676" data-mp="29665pfod6v43">
            <span className="text-lg font-bold text-[#333333]" data-id="element-4677" data-mp="2e59b5189b3cl">$142.5k</span>
            <span className="text-[9px] text-red-500" data-id="element-4678" data-mp="xriwx1nfayp8">+12% MoM</span>
          </div>
          <div className="flex flex-col gap-2" data-id="element-4679" data-mp="xtvng378yhdw">
            <div className="flex justify-between items-center" data-id="element-4680" data-mp="12rne6tw5nxgi">
              <span className="text-[10px] text-neutral-500" data-id="element-4681" data-mp="u9lebh9j1r9y">AWS</span>
              <span className="text-[10px] text-[#333333] font-medium" data-id="element-4682" data-mp="u7sae9qatl5n">
                $45k
              </span>
            </div>
            <div className="flex justify-between items-center" data-id="element-4683" data-mp="147j2y5vatq33">
              <span className="text-[10px] text-neutral-500" data-id="element-4684" data-mp="146ztzenmmc17">Salesforce</span>
              <span className="text-[10px] text-[#333333] font-medium" data-id="element-4685" data-mp="z9zop5ylpdb5">
                $22k
              </span>
            </div>
            <div className="flex justify-between items-center" data-id="element-4686" data-mp="bh0yp1iu9io3">
              <span className="text-[10px] text-neutral-500" data-id="element-4687" data-mp="6lbx6sn9drp5">Datadog</span>
              <span className="text-[10px] text-[#333333] font-medium" data-id="element-4688" data-mp="l96s8foqo85e">
                $18k
              </span>
            </div>
          </div>
        </div>
  }, {
    title: 'Headcount Plan',
    description: 'Open roles vs budget, pulling from Greenhouse and Workday.',
    icon: <Users size={16} className="text-neutral-400" data-id="element-4689" data-mp="3jyx602m5crax" />,
    connections: 2,
    uiPreview: <div className="flex flex-col gap-3 h-full" data-id="element-4690" data-mp="3jx2vli8i4hl3">
          <div className="grid grid-cols-2 gap-2 mb-2" data-id="element-4691" data-mp="1nupt4ukhh0sd">
            <div className="flex flex-col" data-id="element-4692" data-mp="1suvksaqr6nag">
              <span className="text-[9px] text-neutral-500" data-id="element-4693" data-mp="1stkjqiooztn1">Open Roles</span>
              <span className="text-sm font-bold text-[#333333]" data-id="element-4694" data-mp="1xl2bqh8qmjvv">14</span>
            </div>
            <div className="flex flex-col" data-id="element-4695" data-mp="21np4ckd8g8u4">
              <span className="text-[9px] text-neutral-500" data-id="element-4696" data-mp="21mnuukbv7uei">Offers Out</span>
              <span className="text-sm font-bold text-[#433832]" data-id="element-4697" data-mp="14xy9ul4xylob">3</span>
            </div>
          </div>
          <div className="flex flex-col gap-1.5" data-id="element-4698" data-mp="19wsrvkv3467d">
            <span className="text-[9px] text-neutral-500" data-id="element-4699" data-mp="1dxl14e3yib3n">Engineering (8)</span>
            <div className="h-1.5 w-full bg-neutral-100 rounded-full overflow-hidden" data-id="element-4700" data-mp="1dzph3uf4r0ka">
              <div className="h-full bg-indigo-500 w-[70%]" data-id="element-4701" data-mp="1ixzb41p4bv4b" />
            </div>
            <span className="text-[9px] text-neutral-500 mt-1" data-id="element-4702" data-mp="2pbykb5nrm2dc">Sales (4)</span>
            <div className="h-1.5 w-full bg-neutral-100 rounded-full overflow-hidden" data-id="element-4703" data-mp="2ub1t916v8sql">
              <div className="h-full bg-indigo-500 w-[30%]" data-id="element-4704" data-mp="34gb5q2plbiyt" />
            </div>
          </div>
        </div>
  }],
  Leadership: [{
    title: 'Business Pulse',
    description: 'Revenue, pipeline, and runway in one glance – pulled live from Stripe and your CRM.',
    icon: <Gauge size={16} className="text-neutral-400" data-id="element-4705" data-mp="2unvq3c1rn6h3" />,
    connections: 4,
    uiPreview: <div className="flex flex-col gap-3 h-full" data-id="element-4706" data-mp="2qo4p6fiiz9yu">
          <div className="grid grid-cols-3 gap-2 mb-1" data-id="element-4707" data-mp="2lpti2cjm7dkb">
            <div className="flex flex-col" data-id="element-4708" data-mp="2ln78y8h2spzz">
              <span className="text-[9px] text-neutral-500" data-id="element-4709" data-mp="2gpxluzh5sqfb">MRR</span>
              <span className="text-sm font-bold text-[#333333]" data-id="element-4710" data-mp="2br47z5xymxem">$182k</span>
              <span className="text-[8px] text-green-600" data-id="element-4711" data-mp="2bt6uvh3tgaf4">+8% MoM</span>
            </div>
            <div className="flex flex-col" data-id="element-4712" data-mp="27rbv4h1ja53g">
              <span className="text-[9px] text-neutral-500" data-id="element-4713" data-mp="9zhcar34iebt">Pipeline</span>
              <span className="text-sm font-bold text-[#333333]" data-id="element-4714" data-mp="9xeq6i53d5cd">$2.4M</span>
              <span className="text-[8px] text-neutral-400" data-id="element-4715" data-mp="5yxfxpqdjeqq">42 deals</span>
            </div>
            <div className="flex flex-col" data-id="element-4716" data-mp="d1vjwi5rihls">
              <span className="text-[9px] text-neutral-500" data-id="element-4717" data-mp="i06vaxbph064">Runway</span>
              <span className="text-sm font-bold text-[#333333]" data-id="element-4718" data-mp="hycjq9mbqkep">18mo</span>
              <span className="text-[8px] text-neutral-400" data-id="element-4719" data-mp="404hbm6iqqw5">$3.1M cash</span>
            </div>
          </div>
          <div className="flex items-end gap-1.5 h-12 mt-auto border-b border-neutral-200 pb-1" data-id="element-4720" data-mp="80g00tfbmh4z">
            {[50, 62, 58, 70, 66, 80, 92].map((h, i) => <div key={i} className="flex-1 bg-emerald-400 rounded-t-sm" style={{
          height: `${h}%`
        }} data-id="element-4721" data-mp="3v6p4gutgdenw" data-mpi={i} />)}
          </div>
        </div>
  }, {
    title: 'Weekly Shipped',
    description: 'What every team actually shipped this week – auto-compiled from Linear, GitHub, and Slack.',
    icon: <Activity size={16} className="text-neutral-400" data-id="element-4722" data-mp="37hmpjuq7dzx3" />,
    connections: 5,
    uiPreview: <div className="flex flex-col gap-2 h-full justify-center" data-id="element-4723" data-mp="3cghg5iuewsrr">
          {[{
        team: 'Engineering',
        note: '14 PRs merged',
        c: 'bg-indigo-500'
      }, {
        team: 'Sales',
        note: '6 deals closed',
        c: 'bg-blue-500'
      }, {
        team: 'Marketing',
        note: '2 launches live',
        c: 'bg-pink-500'
      }, {
        team: 'Product',
        note: '3 features out',
        c: 'bg-purple-500'
      }].map((r, i) => <div key={i} className="flex items-center justify-between bg-[#FAF8F4] border border-neutral-100 rounded-md px-2.5 py-1.5" data-id="element-4724" data-mp="2xs7f37ffhwe0" data-mpi={i}>
              <div className="flex items-center gap-2" data-id="element-4725" data-mp="36ugkxjzr51l2">
                <span className={`w-1.5 h-1.5 rounded-full ${r.c}`} data-id="element-4726" data-mp="36txdix7q9vla" />
                <span className="text-[10px] font-medium text-[#333333]" data-id="element-4727" data-mp="3ljmkj92i4tth">
                  {r.team}
                </span>
              </div>
              <span className="text-[10px] text-neutral-500" data-id="element-4728" data-mp="3gl1q5q9xl4cy">{r.note}</span>
            </div>)}
        </div>
  }],
  Product: [{
    title: 'Roadmap Health',
    description: 'Every initiative with live status, owners, and risk – synced from Linear and Jira.',
    icon: <Map size={16} className="text-neutral-400" data-id="element-4729" data-mp="2n8lax2hwy7a4" />,
    connections: 3,
    uiPreview: <div className="flex flex-col gap-2.5 h-full justify-center" data-id="element-4730" data-mp="2s63lexbkwuk8">
          {[{
        name: 'Billing v2',
        status: 'On track',
        c: 'text-blue-600 bg-blue-50 border-blue-100',
        w: '70%',
        bar: 'bg-blue-500'
      }, {
        name: 'Mobile app',
        status: 'At risk',
        c: 'text-orange-600 bg-orange-50 border-orange-100',
        w: '35%',
        bar: 'bg-orange-500'
      }, {
        name: 'SSO / SAML',
        status: 'Shipped',
        c: 'text-green-700 bg-green-50 border-green-100',
        w: '100%',
        bar: 'bg-green-500'
      }].map((r, i) => <div key={i} className="flex flex-col gap-1" data-id="element-4731" data-mp="3jyb3m26qgf0q" data-mpi={i}>
              <div className="flex items-center justify-between" data-id="element-4732" data-mp="3jvova0c0pvhg">
                <span className="text-[10px] font-medium text-[#333333]" data-id="element-4733" data-mp="1ljhv4myefl3u">
                  {r.name}
                </span>
                <span className={`text-[9px] px-1.5 py-0.5 rounded border ${r.c}`} data-id="element-4734" data-mp="1pk1r3n78kioe">
                  {r.status}
                </span>
              </div>
              <div className="h-1.5 w-full bg-neutral-100 rounded-full overflow-hidden" data-id="element-4735" data-mp="1d9afz3ovufdb">
                <div className={`h-full ${r.bar}`} style={{
            width: r.w
          }} data-id="element-4736" data-mp="189xtwl5u5cbs" />
              </div>
            </div>)}
        </div>
  }, {
    title: 'Feedback Themes',
    description: 'Top themes from support, reviews, and calls – clustered automatically.',
    icon: <MessageCircle size={16} className="text-neutral-400" data-id="element-4737" data-mp="1w00ksxy0kc8t" />,
    connections: 4,
    uiPreview: <div className="flex flex-col gap-2.5 h-full justify-center" data-id="element-4738" data-mp="1vy7ddgfktnzr">
          {[{
        theme: 'Onboarding friction',
        n: 42,
        w: '100%'
      }, {
        theme: 'Mobile bugs',
        n: 28,
        w: '66%'
      }, {
        theme: 'Pricing confusion',
        n: 17,
        w: '40%'
      }].map((r, i) => <div key={i} className="flex flex-col gap-1" data-id="element-4739" data-mp="bkgfnhssehyh" data-mpi={i}>
              <div className="flex items-center justify-between" data-id="element-4740" data-mp="bivm9vi7l527">
                <span className="text-[10px] text-[#333333]" data-id="element-4741" data-mp="2bld8d8tg08va">{r.theme}</span>
                <span className="text-[10px] font-bold text-neutral-500" data-id="element-4742" data-mp="2fojdue8qdw0p">
                  {r.n}
                </span>
              </div>
              <div className="h-1.5 w-full bg-neutral-100 rounded-full overflow-hidden" data-id="element-4743" data-mp="21ptfkva6dqar">
                <div className="h-full bg-pink-400" style={{
            width: r.w
          }} data-id="element-4744" data-mp="26o4rdbk16avm" />
              </div>
            </div>)}
        </div>
  }],
  'Customer Success': [{
    title: 'Churn Radar',
    description: 'At-risk accounts ranked by health score – from product usage, tickets, and CRM.',
    icon: <HeartPulse size={16} className="text-neutral-400" data-id="element-4745" data-mp="2kml71rzvfbpw" />,
    connections: 4,
    uiPreview: <div className="flex flex-col gap-3 h-full" data-id="element-4746" data-mp="2pkwgw2q61m84">
          <div className="grid grid-cols-3 gap-2 border-b border-neutral-200 pb-2" data-id="element-4747" data-mp="3dbs6ct75g5c7">
            <span className="text-[9px] text-neutral-500 col-span-2" data-id="element-4748" data-mp="3d9ywz655j70i">
              ACCOUNT
            </span>
            <span className="text-[9px] text-neutral-500" data-id="element-4749" data-mp="16njdj3b2nze0">HEALTH</span>
          </div>
          {[{
        name: 'Initech',
        score: 41,
        c: 'bg-red-500',
        pill: 'text-red-600 bg-red-50 border-red-100',
        risk: 'Critical'
      }, {
        name: 'Acme Corp',
        score: 62,
        c: 'bg-yellow-500',
        pill: 'text-orange-600 bg-orange-50 border-orange-100',
        risk: 'At risk'
      }, {
        name: 'Globex',
        score: 88,
        c: 'bg-green-500',
        pill: 'text-green-700 bg-green-50 border-green-100',
        risk: 'Healthy'
      }].map((r, i) => <div key={i} className="grid grid-cols-3 gap-2 items-center" data-id="element-4750" data-mp="wpc18xrqa5ic" data-mpi={i}>
              <div className="flex items-center gap-1.5 col-span-2" data-id="element-4751" data-mp="wn8zjgh7qnxs">
                <span className={`w-1.5 h-1.5 rounded-full ${r.c}`} data-id="element-4752" data-mp="rphbixjovlyv" />
                <span className="text-[10px] text-[#333333] font-medium truncate" data-id="element-4753" data-mp="283zafcwl03xq">
                  {r.name}
                </span>
              </div>
              <span className={`text-[9px] px-1.5 py-0.5 rounded border ${r.pill} text-center`} data-id="element-4754" data-mp="2355u7cpwtytv">
                {r.score}
              </span>
            </div>)}
        </div>
  }, {
    title: 'Ticket Pulse',
    description: 'Live support volume, CSAT, and SLA – pulled from Intercom and Zendesk.',
    icon: <Ticket size={16} className="text-neutral-400" data-id="element-4755" data-mp="22s4u3fj05gdl" />,
    connections: 3,
    uiPreview: <div className="flex flex-col gap-3 h-full" data-id="element-4756" data-mp="1xtjv1jfhl2qq">
          <div className="flex justify-between items-end mb-1" data-id="element-4757" data-mp="2bue3g22jb3pe">
            <div className="flex flex-col" data-id="element-4758" data-mp="2bst7q94hhepy">
              <span className="text-[10px] text-neutral-500" data-id="element-4759" data-mp="26wkzlfopelsa">Open</span>
              <span className="text-sm font-bold text-[#333333]" data-id="element-4760" data-mp="2lkphwijugvaj">37</span>
            </div>
            <div className="flex flex-col text-center" data-id="element-4761" data-mp="2lmjxdhccjv78">
              <span className="text-[10px] text-neutral-500" data-id="element-4762" data-mp="2gmxg8wtedut5">CSAT</span>
              <span className="text-sm font-bold text-green-600" data-id="element-4763" data-mp="2ukvqbq0or52u">94%</span>
            </div>
            <div className="flex flex-col text-right" data-id="element-4764" data-mp="2qkbwowfbmjlg">
              <span className="text-[10px] text-neutral-500" data-id="element-4765" data-mp="2qlmxcndgzb8c">SLA</span>
              <span className="text-sm font-bold text-[#333333]" data-id="element-4766" data-mp="399dzlg0ukilg">98%</span>
            </div>
          </div>
          <div className="flex items-end gap-1.5 h-12 mt-auto border-b border-neutral-200 pb-1" data-id="element-4767" data-mp="3e5vyhz9hxmpv">
            {[60, 45, 70, 50, 80, 40, 55].map((h, i) => <div key={i} className="flex-1 bg-blue-400 rounded-t-sm" style={{
          height: `${h}%`
        }} data-id="element-4768" data-mp="356hcpd49y0bp" data-mpi={i} />)}
          </div>
        </div>
  }]
};
const personas: Persona[] = ['Leadership', 'Sales', 'Marketing', 'Product', 'Engineering', 'Customer Success', 'Operations'];
const personaPrompt: Record<Persona, string> = {
  Leadership: 'build me a live business pulse and a weekly cross-team shipped report',
  Sales: 'build me a live pipeline tracker and a deal war room from our CRM',
  Marketing: 'build me a campaign ROI dashboard across Meta, Google, and LinkedIn',
  Product: 'build me a roadmap health board and cluster our user feedback themes',
  Engineering: 'build me an incident command center from Sentry and Datadog',
  'Customer Success': 'build me a churn radar and a live support ticket dashboard',
  Operations: 'build me a vendor spend tracker and headcount plan from Xero and Greenhouse'
};
export function ProductDemo() {
  const copy = useCopy();
  const [activePersona, setActivePersona] = useState<Persona>('Leadership');
  return <section className="bg-[#FAF8F4] py-12 md:py-32 border-t border-neutral-200 relative overflow-hidden" data-id="element-4769" data-mp="4bi2uow5ekl9">
      {/* Halftone dot-matrix texture — matches the other cream sections */}
      <div aria-hidden="true" className="absolute inset-0 dot-matrix opacity-40 pointer-events-none z-0" data-id="element-4770" data-mp="wzxed1g2mkru" />
      {/* Subtle background glow */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-[#F97316]/10 blur-[120px] rounded-full pointer-events-none z-0" data-id="element-4771" data-mp="s1vr04bm3zak" />

      <div className="max-w-[1280px] mx-auto px-6 md:px-10 relative z-10" data-id="element-4772" data-mp="3cr1nx0mo0wp8">
        <div className="text-left md:text-center mb-12 md:mb-16" data-id="element-4773" data-mp="37qe00ntqgvz8">
          <span className="block text-xs font-semibold tracking-[5px] uppercase text-neutral-400 mb-5 md:mb-6" data-id="element-4774" data-mp="37t03ocdi6rc6">
            Your command center
          </span>
          <h2 className="text-3xl md:text-5xl font-title font-medium tracking-tight leading-[1.1] text-[#2B211A] mb-5 md:mb-6" data-id="element-4775" data-mp="32mv1y5xcq6we">
            {copy.productDemo.headline}
          </h2>
          <p className="text-base md:text-xl text-neutral-500 leading-relaxed max-w-2xl mx-auto mb-8 md:mb-10" data-id="element-4776" data-mp="3cc4shhcjrhie">
            Bring workflows, automation, and context into one place.
          </p>

          {/* Persona Tabs */}
          <div className="flex overflow-x-auto md:flex-wrap justify-start md:justify-center gap-2 md:gap-4 pb-2 md:pb-0 -mx-6 px-6 md:mx-0 md:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-id="element-4777" data-mp="3lazzgvnf0vmr">
            {personas.map(persona => {
            const isActive = activePersona === persona;
            return <button key={persona} onClick={() => setActivePersona(persona)} className={`shrink-0 whitespace-nowrap px-4 py-2 md:px-5 md:py-2.5 rounded-full text-sm font-medium transition-all duration-200 ${isActive ? 'bg-white text-[#433832] shadow-sm border border-[#EAE4D9]' : 'text-neutral-500 hover:text-[#433832] hover:bg-white/60 border border-transparent'}`} data-id="element-4778" data-mp="1vz7pow1maqji">
                  {persona}
                </button>;
          })}
          </div>
        </div>

        {/* Product Window Shell */}
        <div className="w-full rounded-[1.5rem] border border-[#EAE4D9] bg-white shadow-2xl shadow-orange-900/5 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-id="element-4779" data-mp="3vg8212fsvjyd">
          <div className="flex flex-row min-w-[900px] min-h-[500px]" data-id="element-4780" data-mp="33pk0hpa7sck6">
            {/* Left Sidebar */}
            <div className="w-56 bg-[#FAF8F4] border-r border-[#EAE4D9] p-4 flex flex-col gap-6 shrink-0" data-id="element-4781" data-mp="3hh8ddusaf527">
              <div className="flex items-center justify-between px-2" data-id="element-4782" data-mp="3cndu2sglgs3n">
                <div className="flex items-center gap-2" data-id="element-4783" data-mp="2oyb672vda30p">
                  <div className="w-5 h-5 rounded bg-[#F97316] flex items-center justify-center" data-id="element-4784" data-mp="2k09nwk5rm7qa">
                    <span className="text-[#433832] font-bold text-[10px]" data-id="element-4785" data-mp="zi8vihd52svc">
                      A
                    </span>
                  </div>
                  <span className="text-[#433832] font-bold text-sm" data-id="element-4786" data-mp="149qytc2mx5jh">Acme</span>
                </div>
                <AnimatePresence mode="wait" data-id="element-4787" data-mp="qiu51hwz2iau">
                  <motion.span key={activePersona} initial={{
                  opacity: 0,
                  scale: 0.9
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} exit={{
                  opacity: 0,
                  scale: 0.9
                }} transition={{
                  duration: 0.2
                }} className="text-[9px] font-bold uppercase tracking-wider bg-white border border-[#EAE4D9] text-neutral-500 px-1.5 py-0.5 rounded-md" data-id="element-4788" data-mp="vgnc0d63ngc6">
                    {activePersona}
                  </motion.span>
                </AnimatePresence>
              </div>

              <nav className="flex flex-col gap-1" data-id="element-4789" data-mp="2s892p08soh7">
                <div className="px-2 py-1.5 text-[10px] font-bold text-neutral-400 uppercase tracking-wider mb-1" data-id="element-4790" data-mp="2pls5yrgowmb">
                  Workspace
                </div>
                <button className="flex items-center gap-3 px-2 py-2.5 rounded-lg text-neutral-600 hover:text-[#433832] hover:bg-orange-50/50 transition-colors text-sm font-medium" data-id="element-4791" data-mp="3auxruad3buna">
                  <LayoutGrid size={16} className="text-neutral-400" data-id="element-4792" data-mp="312s2z9i4xtcb" />
                  Home
                </button>
                <button className="flex items-center gap-3 px-2 py-2.5 rounded-lg bg-white text-[#433832] border border-[#EAE4D9] shadow-sm transition-colors text-sm font-medium w-full text-left" data-id="element-4793" data-mp="2w6bf8geulgzr">
                  <LayoutGrid size={16} className="shrink-0 text-[#F97316]" data-id="element-4794" data-mp="2n4v76fbi9qyp" />
                  <span className="flex-1" data-id="element-4795" data-mp="2i5qsj95tb10z">Dashboards</span>
                  <span className="text-[9px] font-bold uppercase tracking-wider bg-[#FFEDD5] text-[#EA580C] px-1.5 py-0.5 rounded-full shrink-0" data-id="element-4796" data-mp="2i7bn2ys4mjyu">
                    Soon
                  </span>
                </button>
                <button className="flex items-center gap-3 px-2 py-2.5 rounded-lg text-neutral-600 hover:text-[#433832] hover:bg-orange-50/50 transition-colors text-sm font-medium w-full text-left" data-id="element-4797" data-mp="nxn2gsv4dk1d">
                  <MessageSquare size={16} className="shrink-0 text-neutral-400" data-id="element-4798" data-mp="xmepqnp8jg5y" />
                  <span className="flex-1" data-id="element-4799" data-mp="12m1cbnqye8rf">Chat</span>
                </button>
                <button className="flex items-center gap-3 px-2 py-2.5 rounded-lg text-neutral-600 hover:text-[#433832] hover:bg-orange-50/50 transition-colors text-sm font-medium w-full text-left" data-id="element-4800" data-mp="12o40e28jprti">
                  <FolderOpen size={16} className="shrink-0 text-neutral-400" data-id="element-4801" data-mp="9zgdy3c28wed" />
                  <span className="flex-1" data-id="element-4802" data-mp="euvpupqici85">Files</span>
                  <span className="text-[9px] font-bold uppercase tracking-wider bg-[#FFEDD5] text-[#EA580C] px-1.5 py-0.5 rounded-full shrink-0" data-id="element-4803" data-mp="iyjvv3zhr5ra">
                    Soon
                  </span>
                </button>
                <button className="flex items-center gap-3 px-2 py-2.5 rounded-lg text-neutral-600 hover:text-[#433832] hover:bg-orange-50/50 transition-colors text-sm font-medium w-full text-left" data-id="element-4804" data-mp="1uc5wblaswts4">
                  <Plug size={16} className="shrink-0 text-neutral-400" data-id="element-4805" data-mp="2bgpeuzewvfwm" />
                  <span className="flex-1" data-id="element-4806" data-mp="2q52dy59apevc">Integrations</span>
                </button>
              </nav>

              {/* Contextual Persona Nav */}
              <nav className="flex flex-col gap-1" data-id="element-4807" data-mp="2lcizz76p4hq0">
                <AnimatePresence mode="wait" data-id="element-4808" data-mp="2z4pekou7agnc">
                  <motion.div key={activePersona} initial={{
                  opacity: 0,
                  y: 4
                }} animate={{
                  opacity: 1,
                  y: 0
                }} exit={{
                  opacity: 0,
                  y: -4
                }} transition={{
                  duration: 0.25
                }} className="flex flex-col gap-1" data-id="element-4809" data-mp="2z3e3e3yfe6m7">
                    <div className="px-2 py-1.5 text-[10px] font-bold text-neutral-400 uppercase tracking-wider mb-1" data-id="element-4810" data-mp="1epnjp98h113x">
                      {activePersona}
                    </div>
                    {personaData[activePersona].map((app, idx) => <button key={idx} className={`flex items-center gap-3 px-2 py-2 rounded-lg text-sm font-medium w-full text-left transition-colors ${idx === 0 ? 'bg-orange-50/80 text-[#433832] border border-orange-100/50 shadow-sm' : 'text-neutral-500 hover:text-[#433832] hover:bg-orange-50/50 border border-transparent'}`} data-id="element-4811" data-mp="3qcr00edgnjsk" data-mpi={idx}>
                        <div className={`shrink-0 scale-90 ${idx === 0 ? 'opacity-100' : 'opacity-70'}`} data-id="element-4812" data-mp="32mwn5wt55v8b">
                          {app.icon}
                        </div>
                        <span className="flex-1 truncate" data-id="element-4813" data-mp="2sp8hibtzz3bd">{app.title}</span>
                      </button>)}
                  </motion.div>
                </AnimatePresence>
              </nav>
            </div>

            {/* Main Content Area */}
            <div className="flex-1 p-6 md:p-8 bg-[#FFFCF8]" data-id="element-4814" data-mp="qyx0iru59ceg">
              <div className="flex flex-row justify-between items-center gap-4 mb-6" data-id="element-4815" data-mp="vwgio2tyq9bo">
                <div className="flex flex-wrap items-center gap-2" data-id="element-4816" data-mp="1xocu4n8xl3sa">
                  <h3 className="text-xl font-bold text-[#433832]" data-id="element-4817" data-mp="1xq5zlz7t5hyp">
                    Dashboards
                  </h3>
                  <span className="text-[10px] font-bold uppercase tracking-wider bg-[#FFEDD5] text-[#EA580C] px-2 py-0.5 rounded-full ml-1" data-id="element-4818" data-mp="22l3a7akqjzdp">
                    Preview · Coming soon
                  </span>
                  <span className="inline text-neutral-400 ml-1" data-id="element-4819" data-mp="2hcc0zn0djqsf">/</span>
                  <button className="flex items-center gap-1 text-[#433832] font-medium hover:text-[#F97316] transition-colors" data-id="element-4820" data-mp="2heeskbh6drz9">
                    {activePersona} <ChevronDown size={16} data-id="element-4821" data-mp="2qcqqyzoa9u29" />
                  </button>
                </div>
                <div className="flex gap-3 w-auto" data-id="element-4822" data-mp="2vcmw528pvrje">
                  <button className="flex items-center gap-2 px-3 py-2 rounded-lg border border-[#EAE4D9] text-[#433832] text-sm font-medium hover:bg-orange-50/50 transition-colors" data-id="element-4823" data-mp="2vbbqewvpo9pn">
                    <Upload size={14} className="text-neutral-400" data-id="element-4824" data-mp="10ylxahd634jb" />
                    Create from files
                  </button>
                  <button className="flex flex-none items-center justify-center gap-2 px-4 py-2 rounded-lg bg-[#F97316] text-white text-sm font-medium hover:bg-[#EA580C] transition-colors shadow-sm shadow-orange-500/20" data-id="element-4825" data-mp="tge2h2kvyagq">
                    <Plus size={14} data-id="element-4826" data-mp="epwwd5xb2wdi" />
                    Create new
                  </button>
                </div>
              </div>

              {/* @Giga prompt that produced these apps — bridges Slack → workspace */}
              <div className="mb-6 rounded-2xl bg-[#FAF8F4] border border-[#EAE4D9] p-5 shadow-sm" data-id="element-4827" data-mp="17fdqc80kqlbu">
                <div className="flex items-start gap-3" data-id="element-4828" data-mp="12e7wu7z3au19">
                  <div className="w-8 h-8 rounded-xl bg-[#3F0E40] flex items-center justify-center shrink-0 shadow-sm" data-id="element-4829" data-mp="xg6b1faa4gm3">
                    <MessageSquare size={16} className="text-white" data-id="element-4830" data-mp="3n5zxstxtrote" />
                  </div>
                  <div className="min-w-0 flex-1" data-id="element-4831" data-mp="3i5vq9tewa4fb">
                    <span className="text-[11px] text-neutral-500 block mb-1 font-medium" data-id="element-4832" data-mp="3i52gskojx0th">
                      Magic from Slack
                    </span>
                    <AnimatePresence mode="wait" data-id="element-4833" data-mp="2e8tc49r60s4o">
                      <motion.p key={activePersona} initial={{
                      opacity: 0,
                      y: 4
                    }} animate={{
                      opacity: 1,
                      y: 0
                    }} exit={{
                      opacity: 0,
                      y: -4
                    }} transition={{
                      duration: 0.25
                    }} className="text-[15px] text-[#433832] leading-snug" data-id="element-4834" data-mp="2e6fi84881lyp">
                        <span className="text-[#1264A3] font-medium" data-id="element-4835" data-mp="1hjc1dgdbsfw4">
                          @Giga
                        </span>{' '}
                        {personaPrompt[activePersona]}
                      </motion.p>
                    </AnimatePresence>
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0 self-center rounded-full bg-white border border-[#EAE4D9] px-3 py-1.5 shadow-sm" data-id="element-4836" data-mp="1cjfv1ah5ocde">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#EA580C]" data-id="element-4837" data-mp="szgzvb8x8raq" />
                    <span className="text-[11px] font-bold text-[#EA580C]" data-id="element-4838" data-mp="oxdladqyh6l8">
                      Giga built {personaData[activePersona].length} dashboards
                    </span>
                  </div>
                </div>
              </div>

              <AnimatePresence mode="wait" data-id="element-4839" data-mp="1bw0doduo73dm">
                <motion.div key={activePersona} initial={{
                opacity: 0,
                y: 10
              }} animate={{
                opacity: 1,
                y: 0
              }} exit={{
                opacity: 0,
                y: -10
              }} transition={{
                duration: 0.3
              }} className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-4840" data-mp="1bup8qaphnpkx">
                  {personaData[activePersona].map((app, idx) => <div key={idx} className="group bg-white border border-[#EAE4D9] rounded-2xl overflow-hidden hover:border-orange-200 transition-all duration-300 flex flex-col shadow-sm hover:shadow-md hover:-translate-y-0.5" data-id="element-4841" data-mp="1vhvdimsx4b99" data-mpi={idx}>
                      {/* App UI Mockup Area */}
                      <div className="h-48 bg-[#FAF8F4] border-b border-[#EAE4D9] p-4 relative overflow-hidden" data-id="element-4842" data-mp="3p0vl2y1cgpen">
                        {/* Top browser-like bar */}
                        <div className="flex items-center gap-2 mb-4" data-id="element-4843" data-mp="2mca7bbkvscx">
                          {app.icon}
                          <span className="text-xs font-bold text-[#433832]" data-id="element-4844" data-mp="316axy27z1sdh">
                            {app.title}
                          </span>
                        </div>
                        {/* The actual UI preview */}
                        <div className="absolute top-12 left-4 right-4 bottom-0 bg-white border border-[#EAE4D9] border-b-0 rounded-t-xl p-4 shadow-sm" data-id="element-4845" data-mp="3b5jrwv2u76zu">
                          {app.uiPreview}
                        </div>
                      </div>

                      {/* App Meta */}
                      <div className="p-5 flex flex-col flex-1" data-id="element-4846" data-mp="ve5mwssfhvpd">
                        <h4 className="text-base font-bold text-[#433832] mb-2" data-id="element-4847" data-mp="2fssc5l4kgxbt">
                          {app.title}
                        </h4>
                        <p className="text-sm text-neutral-600 mb-6 flex-1" data-id="element-4848" data-mp="264agfib59gf1">
                          {app.description}
                        </p>

                        <div className="flex items-center justify-between mt-auto pt-4 border-t border-neutral-100" data-id="element-4849" data-mp="214e9pbb2bavs">
                          <span className="text-xs text-neutral-500 font-medium" data-id="element-4850" data-mp="1w7fhiyayle08">
                            {app.connections} Connections · Active
                          </span>
                          {/* Slack Tie-in */}
                          <div className="flex items-center gap-1.5 bg-[#1264A3]/5 px-2 py-1 rounded text-[10px] font-medium text-[#1264A3] border border-[#1264A3]/10" data-id="element-4851" data-mp="1i6bgekbhers6">
                            <span data-id="element-4852" data-mp="3hinz88r1j5o7">@Giga</span>
                            <span className="text-[#1264A3]/70 font-normal" data-id="element-4853" data-mp="3cjukkd00p8mg">
                              synced to Slack
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>)}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>;
}